/*
  Product badges — the one driver behind every badge row.

  snippets/product-badges.liquid publishes each colour's state in `data-badge-groups` and
  renders a pill for every state the product can reach, hidden where the colour on show is
  not in it. This reveals them again on `product-badges:update`, so the PDP's variant
  picker and a card's colour swatch drive the same markup through the same path.

  Contract
    Dispatch `product-badges:update` on any element that contains a badge row:

      el.dispatchEvent(new CustomEvent('product-badges:update', {
        bubbles: true,
        detail: { group: 'Black' }
      }));

    `group` is a colour option value. A caller holding a variant rather than a colour sends
    `{ options: variant.options }` and `[data-badge-group-option]` picks the colour out of
    it; a product with no colour option publishes a single group and either form finds it.

    Status is the colour's — sold out means every size of it has gone. The sale figure has
    to match the price the surface shows, so a caller pricing one variant sends
    `{ onSale, discount }` alongside and those win for that pill.
*/
(function () {
  'use strict';

  /* Both the grid and the PDP request this file, and a page can hold both. Marking the
     document keeps one listener rather than one per requesting section. */
  var READY = 'data-product-badges-ready';
  if (document.documentElement.hasAttribute(READY)) return;
  document.documentElement.setAttribute(READY, '');

  var EVENT = 'product-badges:update';

  // Parsed once per row: a grid re-render replaces the element, so the cache cannot go
  // stale against its own markup.
  function groupsOf(list) {
    if (list.__badgeGroups) return list.__badgeGroups;

    var parsed = {};
    try {
      parsed = JSON.parse(list.getAttribute('data-badge-groups') || '{}') || {};
    } catch (e) {
      parsed = {};
    }

    list.__badgeGroups = parsed;
    return parsed;
  }

  function stateFor(list, detail) {
    var groups = groupsOf(list);
    var key = detail.group;

    if (key == null && detail.options) {
      var index = parseInt(list.getAttribute('data-badge-group-option'), 10);
      if (index >= 0) key = detail.options[index];
    }

    if (key != null && groups[key]) return groups[key];

    // One group means one answer, whatever was asked for: a product with no colour option
    // has every variant in the same state.
    var keys = Object.keys(groups);
    return keys.length === 1 ? groups[keys[0]] : null;
  }

  function apply(list, detail) {
    var state = stateFor(list, detail);
    if (!state) return;

    var template = list.getAttribute('data-sale-label-template');
    var discount = detail.discount == null ? state.discount : detail.discount;
    /* Read off the figure rather than trusted beside it: a pill revealed without one would
       keep whatever percentage the server rendered. */
    var onSale = (detail.onSale == null ? state.onSale : detail.onSale) && discount > 0;

    var pills = list.querySelectorAll('[data-badge-status]');
    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      switch (pill.getAttribute('data-badge-status')) {
        case 'sold-out':
          pill.hidden = !state.soldOut;
          break;
        case 'coming-soon':
          pill.hidden = !state.comingSoon;
          break;
        case 'low-stock':
          pill.hidden = !state.lowStock;
          break;
        case 'sale':
          pill.hidden = !onSale;
          if (onSale && template) pill.textContent = template.replace('__PCT__', discount);
          break;
      }
    }
  }

  document.addEventListener(EVENT, function (e) {
    var scope = e.target;
    if (!scope || typeof scope.querySelector !== 'function') return;

    /* The dispatcher owns a surface (a form, a card), not the row itself, so look inside
       first and only then upwards for the case where the row is the target. */
    var list = scope.querySelector('[data-product-badges]');
    if (!list && scope.matches && scope.matches('[data-product-badges]')) list = scope;
    if (!list && scope.closest) list = scope.closest('[data-product-badges]');

    if (list) apply(list, e.detail || {});
  });
})();
